/**
 * Copyright (c) 2018-2019 Aleksey Melnikov, True Directive Company.
 * @link https://truedirective.com/
 * @license MIT
*/
import { Directive, ElementRef, Input, HostListener, Renderer2, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { InternationalizationService } from '../internationalization/internationalization.service';
import { Mask } from '@true-directive/base';
import { MaskSettings } from '@true-directive/base';
import { MaskState } from '@true-directive/base';

import { MaskBaseDirective } from './mask-base.directive';

@Directive({
    selector: '[true-mask]',
    host: {'(input)': 'input($event.target.value)', '(blur)': 'blur()'},
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => MaskDirective),
        multi: true}]
})
export class MaskDirective extends MaskBaseDirective implements ControlValueAccessor {

    private onChange = (_: any) => {};
    private onTouched = () => {};

    registerOnChange(fn: (_: any) => void): void { this.onChange = fn; }
    registerOnTouched(fn: () => void): void { this.onTouched = fn; }

    blur() {
      // ÐÑÐ¸ÑÐ°ÐµÐ¼, ÐµÑÐ»Ð¸ Ð¼Ð°ÑÐºÐ° Ð½ÐµÐ²ÐµÑÐ½Ð°
      const autocorrected = this._mask.applyMask(this._txtValue);
      if (autocorrected === '' && !this._mask.settings.allowIncomplete) {
        this.setText('');
      } else {
        // ÐÐ°ÑÐºÐ° Ð²ÐµÑÐ½Ð°, Ð½Ð¾ Ð½ÑÐ¶Ð½Ð¾ Ð°Ð²ÑÐ¾ÐºÐ¾ÑÑÐµÐºÑÐ¸Ñ Ð¿ÑÐ¾Ð²ÐµÑÐ½ÑÑÑ
        if (autocorrected !== this._txtValue) {
          this.setText(autocorrected);
        }
      }

      this.onTouched();
    }

    // ÐÐ¾Ð»ÑÐ·Ð¾Ð²Ð°ÑÐµÐ»Ñ Ð²Ð½Ð¾ÑÐ¸Ñ Ð·Ð½Ð°ÑÐµÐ½Ð¸Ðµ. Parser: View --> Ctrl
    // Ð¢Ð¾Ð»ÑÐºÐ¾ ÑÐ¾, ÑÑÐ¾ Ð½Ðµ Ð¾Ð±ÑÐ°Ð±Ð¾ÑÐ°Ð½Ð¾ Ð¼Ð°ÑÐºÐ¾Ð¹
    input(txt: any) {
      this.doInput(txt);
    }

    // ÐÐ±Ð½Ð¾Ð²Ð»ÑÐµÐ¼ ÑÐ¾ÑÑÐ¾ÑÐ½Ð¸Ðµ
    protected updateState() {
      if (this._txtValue === '') {
        // ÐÑÑÑÐ¾Ðµ Ð·Ð½Ð°ÑÐµÐ½Ð¸Ðµ
        this.state = MaskState.EMPTY;
      } else {
        if (!this._mask.checkMask(this._txtValue)) {
          // Ð¡ÑÐ¸ÑÐ°ÐµÐ¼, ÑÑÐ¾ Ð¿Ð¾Ð»ÑÐ·Ð¾Ð²Ð°ÑÐµÐ»Ñ Ð½Ðµ Ð·Ð°Ð²ÐµÑÑÐ¸Ð» Ð²Ð²Ð¾Ð´
          this.state = MaskState.TYPING;
        } else {
          this.state = MaskState.OK;
        }
      }
    }

    protected toModel(): void {
      // ÐÑÐ¿ÑÐ°Ð²Ð»ÑÐµÐ¼ Ð² Ð¼Ð¾Ð´ÐµÐ»Ñ
      this.onChange(this._txtValue);
      // ÐÐ±Ð½Ð¾Ð²Ð»ÑÐµÐ¼ ÑÐ¾ÑÑÐ¾ÑÐ½Ð¸Ðµ
      this.updateState();
    }

    // ÐÑÐ¾Ð±ÑÐ°Ð¶Ð°ÐµÐ¼ Ð·Ð½Ð°ÑÐµÐ½Ð¸Ðµ Ð² ÐºÐ¾Ð¼Ð¿Ð¾Ð½ÐµÐ½ÑÐµ. Formatter: Ctrl --> View
    writeValue(txt: any): void {
      if (this._txtValue !== txt) {
        this.setText(txt, false); // ÐÐµ Ð¾ÑÐ¿ÑÐ°Ð²Ð»ÑÐµÐ¼ Ð·Ð½Ð°ÑÐµÐ½Ð¸Ðµ Ð² Ð¼Ð¾Ð´ÐµÐ»Ñ, Ñ.Ðº. ÑÑÐ¾Ñ Ð¼ÐµÑÐ¾Ð´ Ð²ÑÐ·ÑÐ²Ð°ÐµÑÑÑ ÐºÐ°Ðº ÑÐ°Ð· Ð¿Ð¾ÑÐ»Ðµ Ð¸Ð·Ð¼ÐµÐ½ÐµÐ½Ð¸Ñ Ð¼Ð¾Ð´ÐµÐ»Ð¸
      }
      // ÐÐ¾ Ð¾Ð±Ð½Ð¾Ð²Ð¸ÑÑ ÑÐ¾ÑÑÐ¾ÑÐ½Ð¸Ðµ Ð½ÑÐ¶Ð½Ð¾...
      this.updateState();
    }

    @Input('true-mask')
    public set pattern(m: string) {

      if (this._txtValue !== '' && this._mask.pattern !== '' && this._mask.pattern !== m) {
        // ÐÐ¾ ÑÐ»Ð¾Ð¶Ð½Ð¾Ð¼Ñ Ð¿ÑÑÐ¸
        let res = this.currentRes();
        let s = this._mask.pureValue(res.newValue);
        this._mask.pattern = m;
        res.newValue = this._mask.applyPureValue(s);
        this.setRes(res);
      } else {
        this._mask.pattern = m;
      }
    }

    public get pattern(): string {
      return this._mask.pattern;
    }

    @Input('true-mask-settings')
    set settings(v: MaskSettings) {
      this._mask.settings = v;
    }

    @HostListener('keydown', ['$event'])
    keyDown(e: any) {
      return this.processKey(e);
    }

    constructor(
      protected _renderer: Renderer2,
      protected _elementRef: ElementRef,
      protected intl: InternationalizationService) {
      super(_renderer, _elementRef, intl);
    }

}
